import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { AIThinkingOrb } from "../components/motion/AIThinkingOrb";
import { Stagger } from "../components/motion/Stagger";
import { NumberTicker } from "../components/motion/NumberTicker";
import { SocialShareBar } from "../components/SocialShareBar";
import { ShareToCommunityButton } from "../components/community/ShareToCommunityButton";
import VideoPlayerDialog from "../components/VideoPlayerDialog";
import { iosSave } from "../lib/iosSave";
import {
  Activity,
  Video,
  TrendingUp,
  Globe,
  RefreshCw,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Sparkles,
  ListChecks,
  Play,
  Download,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_META = {
  video:       { icon: Video,       label: "Video",       accent: "coral" },
  trend_scan:  { icon: TrendingUp,  label: "Trend scan",  accent: "citrus" },
  store_url:   { icon: Globe,       label: "Store URL",   accent: "ink" },
};

const STATUS_META = {
  pending:    { label: "Queued",      tone: "muted",   inFlight: true  },
  processing: { label: "Generating",  tone: "active",  inFlight: true  },
  running:    { label: "Scanning",    tone: "active",  inFlight: true  },
  evaluating: { label: "Evaluating",  tone: "active",  inFlight: true  },
  completed:  { label: "Completed",   tone: "success", inFlight: false },
  approved:   { label: "Approved",    tone: "success", inFlight: false },
  rejected:   { label: "Rejected",    tone: "danger",  inFlight: false },
  failed:     { label: "Failed",      tone: "danger",  inFlight: false },
};

export default function TasksPage() {
  const { token } = useAuth();
  const [data, setData] = useState({ tasks: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [playerVideo, setPlayerVideo] = useState(null);    // { url, title } | null
  const pollRef = useRef(null);

  const openPlayer = useCallback((url, title) => setPlayerVideo({ url, title }), []);

  const auth = useCallback(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const r = await axios.get(`${API_URL}/api/tasks/me`, auth());
      setData(r.data);
    } catch (e) {
      console.error("tasks load failed", e);
    } finally {
      setLoading(false);
    }
  }, [token, auth]);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 8000);
    return () => clearInterval(pollRef.current);
  }, [load]);

  const handleRetry = async (task) => {
    setRetrying((m) => ({ ...m, [task.id]: true }));
    try {
      await axios.post(`${API_URL}/api/tasks/${task.kind}/${task.id}/retry`, {}, auth());
      toast.success(`Retrying ${KIND_META[task.kind]?.label.toLowerCase()}…`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    } finally {
      setRetrying((m) => ({ ...m, [task.id]: false }));
    }
  };

  const handleDismiss = async (task) => {
    try {
      await axios.delete(`${API_URL}/api/tasks/${task.kind}/${task.id}`, auth());
      setData((d) => ({ ...d, tasks: d.tasks.filter((t) => t.id !== task.id) }));
      toast.success("Dismissed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not dismiss");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <AIThinkingOrb label="Loading tasks" size={80} />
      </div>
    );
  }

  const { tasks = [], summary = {} } = data;
  const inFlight = tasks.filter((t) => STATUS_META[t.status]?.inFlight);
  const finished = tasks.filter((t) => !STATUS_META[t.status]?.inFlight);

  return (
    <div className="space-y-10" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-ink-500">Activity</p>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-[-0.04em] mt-2 leading-[1]">
            Your <span className="text-coral-500">tasks.</span>
          </h1>
          <p className="text-ink-500 font-light mt-3 max-w-lg">
            Every long-running generation, trend scan and URL evaluation lives here. Auto-refreshes every 8 seconds.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          data-testid="tasks-refresh-btn"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      {/* Summary cards */}
      <Stagger className="grid grid-cols-1 sm:grid-cols-3 gap-5">
        <Stagger.Item>
          <SummaryCard
            icon={Activity}
            label="In flight"
            value={summary.in_flight || 0}
            tone="primary"
            testid="summary-in-flight"
          />
        </Stagger.Item>
        <Stagger.Item>
          <SummaryCard
            icon={CheckCircle2}
            label="Completed today"
            value={summary.completed_today || 0}
            tone="accent"
            testid="summary-completed-today"
          />
        </Stagger.Item>
        <Stagger.Item>
          <SummaryCard
            icon={AlertTriangle}
            label="Failed (all-time)"
            value={summary.failed_total || 0}
            tone="muted"
            testid="summary-failed"
          />
        </Stagger.Item>
      </Stagger>

      {/* In-flight section */}
      <Section
        title="Running now"
        subtitle="These tasks are being processed in the background. The bars update as they progress."
        empty={
          inFlight.length === 0 && (
            <EmptyState
              icon={Sparkles}
              title="Nothing running."
              hint="Generate a video, run a trend scan or add a store URL — it'll appear here live."
            />
          )
        }
      >
        <AnimatePresence>
          {inFlight.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onRetry={handleRetry}
              onDismiss={handleDismiss}
              retrying={!!retrying[t.id]}
              onPlay={openPlayer}
            />
          ))}
        </AnimatePresence>
      </Section>

      {/* History section */}
      <Section
        title="Recent history"
        subtitle="Completed and failed tasks. Failed items can be retried for free."
        empty={
          finished.length === 0 && (
            <EmptyState
              icon={ListChecks}
              title="No history yet"
              hint="Once your first task finishes, it shows up here."
            />
          )
        }
      >
        <AnimatePresence>
          {finished.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              onRetry={handleRetry}
              onDismiss={handleDismiss}
              retrying={!!retrying[t.id]}
              onPlay={openPlayer}
            />
          ))}
        </AnimatePresence>
      </Section>

      {/* In-app video player — opened by Play buttons on any TaskRow */}
      <VideoPlayerDialog
        open={!!playerVideo}
        onClose={() => setPlayerVideo(null)}
        url={playerVideo?.url}
        title={playerVideo?.title}
      />
    </div>
  );
}

// --------------------------------------------------------------------------

function SummaryCard({ icon: Icon, label, value, tone, testid }) {
  const palette = {
    primary: "bg-ink-950 text-white",
    accent:  "bg-citrus-500/20 text-ink-950 border border-citrus-500/40",
    muted:   "bg-white border border-ink-200/70 text-ink-950",
  }[tone];
  return (
    <div className={`relative h-full p-7 rounded-3xl overflow-hidden ${palette}`} data-testid={testid}>
      <Icon className="w-5 h-5 mb-6 opacity-90" />
      <p className="font-display font-black text-5xl tracking-[-0.04em] leading-none">
        <NumberTicker value={value} duration={0.7} />
      </p>
      <p className="mt-3 text-xs font-bold uppercase tracking-[0.22em] opacity-80">
        {label}
      </p>
      {tone === "primary" && value > 0 && (
        <span className="absolute top-4 right-4 inline-flex items-center gap-1.5">
          <span className="relative inline-flex h-2.5 w-2.5">
            <span className="absolute inset-0 rounded-full bg-coral-500 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-coral-500" />
          </span>
        </span>
      )}
    </div>
  );
}

function Section({ title, subtitle, empty, children }) {
  return (
    <section>
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="font-display font-black text-2xl tracking-[-0.03em]">{title}</h2>
          <p className="text-sm text-ink-500 font-light">{subtitle}</p>
        </div>
      </div>
      <div className="space-y-3">{empty || children}</div>
    </section>
  );
}

function EmptyState({ icon: Icon, title, hint }) {
  return (
    <Card className="bg-white">
      <CardContent className="py-12 text-center">
        <Icon className="w-10 h-10 mx-auto mb-3 text-ink-300" />
        <p className="font-semibold text-ink-950">{title}</p>
        <p className="text-sm text-ink-500 mt-1">{hint}</p>
      </CardContent>
    </Card>
  );
}

function TaskRow({ task, onRetry, onDismiss, retrying, onPlay }) {
  const kindMeta = KIND_META[task.kind] || KIND_META.video;
  const statusMeta = STATUS_META[task.status] || STATUS_META.processing;
  const KindIcon = kindMeta.icon;

  const statusBadge = {
    success: <Badge variant="success">{statusMeta.label}</Badge>,
    danger:  <Badge variant="destructive">{statusMeta.label}</Badge>,
    active:  <Badge variant="accent" className="!bg-coral-500 !text-white">{statusMeta.label}</Badge>,
    muted:   <Badge variant="secondary">{statusMeta.label}</Badge>,
  }[statusMeta.tone];

  const updated = task.updated_at ? new Date(task.updated_at).toLocaleString() : "—";

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl border border-ink-200/70 bg-white p-5"
      data-testid={`task-row-${task.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-2xl bg-ink-100 flex items-center justify-center">
          <KindIcon className="w-5 h-5 text-ink-950" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">
              {kindMeta.label}
            </span>
            {statusBadge}
            <span className="text-[10px] text-ink-500 flex items-center gap-1">
              <Clock className="w-3 h-3" /> {updated}
            </span>
          </div>
          <p className="mt-1.5 font-semibold text-ink-950 truncate" title={task.title}>
            {task.title || "Untitled"}
          </p>
          {task.subtitle && (
            <p className="text-xs text-ink-500 font-light truncate">{task.subtitle}</p>
          )}
          {task.error && (
            <p className="mt-2 text-xs text-danger font-medium line-clamp-2">
              <AlertTriangle className="inline w-3 h-3 mr-1" />
              {task.error}
            </p>
          )}

          {/* Progress bar */}
          <div className="mt-3 h-1.5 rounded-full bg-ink-100 overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${
                statusMeta.tone === "danger"
                  ? "bg-danger"
                  : statusMeta.tone === "success"
                  ? "bg-coral-500"
                  : "bg-ink-950"
              }`}
              initial={{ width: 0 }}
              animate={{ width: `${task.progress || 0}%` }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2 shrink-0 min-w-[140px]">
          {/* Play / Download for completed videos */}
          {task.kind === "video" && task.status === "completed" && (task.polished_url || task.media_url) && (
            <>
              <Button
                size="sm"
                className="w-full bg-ink-950 hover:bg-ink-900 text-white"
                onClick={() => onPlay?.(task.polished_url || task.media_url, task.title || "Video")}
                data-testid={`task-play-${task.id}`}
              >
                <Play className="w-3.5 h-3.5 mr-1" />
                Play
              </Button>
              <a
                href={task.polished_url || task.media_url}
                target="_blank"
                rel="noopener noreferrer"
                download
                onClick={(e) => {
                  if (/iPad|iPhone|iPod/.test(navigator.userAgent || "")) {
                    e.preventDefault();
                    iosSave(task.polished_url || task.media_url, {
                      kind: "video",
                      filename: `xea-task-${task.id}.mp4`,
                    });
                  }
                }}
                data-testid={`task-download-${task.id}`}
              >
                <Button size="sm" variant="outline" className="w-full">
                  <Download className="w-3.5 h-3.5 mr-1" />
                  Download
                </Button>
              </a>
              <SocialShareBar
                mediaUrl={task.polished_url || task.media_url}
                caption={task.title}
                variant="compact"
                className="mt-1"
              />
              <ShareToCommunityButton
                sourceCollection="video_tasks"
                sourceId={task.id}
                size="sm"
                variant="outline"
                className="w-full border-coral-500/40 text-coral-600 hover:bg-coral-500/10"
                testId={`task-share-to-community-${task.id}`}
              />
            </>
          )}

          {task.retryable && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRetry(task)}
              disabled={retrying}
              data-testid={`task-retry-${task.id}`}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${retrying ? "animate-spin" : ""}`} />
              Retry
            </Button>
          )}
          {!statusMeta.inFlight && task.kind !== "store_url" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDismiss(task)}
              data-testid={`task-dismiss-${task.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              Dismiss
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  );
}
